import PropTypes from 'prop-types';
import { memo, ReactElement, useCallback, useContext, useMemo } from 'react';

import { GenericContext } from 'shared/constants';
import { BaseAttachmentDto, BaseFileTypeDto, BaseFileTypeCategoryDto } from '@eva/types/dto';

import GroupedList, {
  getDefaultGroupState,
  getDefaultRowState,
  useGroupRowStates,
  useRowStates,
  type ControlDescriptor,
  type GroupedTableClassNames,
} from 'components/GroupedList';

import type { GenericContextType } from 'types/generic-context';

// eslint-disable-next-line import/no-cycle
import {
  getFileTypeCategoryId,
  getCategoryIdOfFile,
  getFileId,
  sortFileCategories,
  sortFiles,
  prepareFilesData,
} from '../functions';

import GroupLabel from './GroupLabel';
import FileLabel from './FileLabel';

import './FilesList.scss';

export interface FilesListProps<
  F extends BaseAttachmentDto,
  FT extends BaseFileTypeDto,
  FC extends BaseFileTypeCategoryDto = BaseFileTypeCategoryDto,
> {
  files: F[];
  fileTypes: FT[];
  plain?: boolean;
  placeholder?: ReactElement;
  selectedFile?: F;
  itemControls?: ControlDescriptor<F, FC>[];
  selectFile?: (file: F) => void;
}

const groupedListClassNames: GroupedTableClassNames = {
  itemClassName: 'files-list__item',
  itemContentClassName: 'files-list__item_content',
  itemControlsClassName: 'files-list__item_controls',
  itemTitleClassName: 'files-list__item-title',

  controlClassName: 'files-list__control',
  controlsClassName: 'files-list__controls',
};

const FilesList = <F extends BaseAttachmentDto, FT extends BaseFileTypeDto>({
  plain,
  files,
  fileTypes,
  placeholder,
  selectedFile,
  itemControls,
  selectFile,
}: FilesListProps<F, FT>) => {
  const { settings } = useContext(GenericContext) as GenericContextType;

  const [filesWithCategories, , categories] = useMemo(
    () => (plain ? [files] : prepareFilesData(files, fileTypes)),
    [plain, files, fileTypes],
  );

  const categoriesWithEmpty = useMemo(() => [{}, ...(categories || [])], [categories]);

  const { rowStates, updateRowState } = useRowStates(files, getFileId, getDefaultRowState);

  const { groupStates, collapse, expand } = useGroupRowStates(
    categoriesWithEmpty,
    getFileTypeCategoryId,
    getDefaultGroupState,
  );

  const getFileLabel = useCallback(
    (file: F) => <FileLabel file={file} dateFormat={settings.dateFormats.dateFormat} onFileSelected={selectFile} />,
    [settings.dateFormats, selectFile],
  );

  const getGroupLabel = useCallback((group) => <GroupLabel group={group} />, []);

  return (
    <GroupedList
      hideEmpty
      plain={plain}
      groups={categories}
      itemStates={rowStates}
      groupStates={groupStates}
      placeholder={placeholder}
      selectedItem={selectedFile}
      items={filesWithCategories}
      itemControls={itemControls}
      classNames={groupedListClassNames}
      getItems={sortFiles}
      getGroups={sortFileCategories}
      getItemId={getFileId}
      getGroupId={getFileTypeCategoryId}
      getItemLabel={getFileLabel}
      getGroupLabel={getGroupLabel}
      getItemGroupId={getCategoryIdOfFile}
      onExpand={expand}
      onCollapse={collapse}
      updateItemState={updateRowState}
    />
  );
};

FilesList.contextTypes = {
  settings: PropTypes.object.isRequired,
};

// eslint-disable-next-line import/no-default-export
export default memo(FilesList);
